import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import styles from './upload-modal.scss';
import FileDropZone from '../../../../../../../../../components/FileDropZone.js';
import closeIcon from '../../../../../../../../../assets/close.svg';
import checkIcon from '../../../../../../../../../assets/check.svg';
import failIcon from '../../../../../../../../../assets/orders-table-reject.svg';
import ReportsService from '../../../../../../../../../services/reports-service.js';
import { IdentityContext } from '../../../../../../../../../context/identity-context.js';

const reportsService = new ReportsService();
const EXCEL_REPORT_TITLE = "Entitlement Report Excel Version";

const UploadModalContent = ({ context, onClose }) => {
  const { report, item } = context || {};
  const [fileState, setFileState] = useState(null);
  const [uploadFlag, setUploadFlag] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ msg: '', show: false });
  const [errorMessage, setErrorMessage] = useState({ msg: '', show: false });
  const [isMounted, setIsMounted] = useState(false);
  const [excelUploaded, setExcelUploaded] = useState(false);
  const reloadTimerRef = useRef(null);

  const { user } = useContext(IdentityContext);
  const makerName = user.attributes['custom:display_name'];

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
      }
    };
  }, []);

  const formatDate = (dt) => new Date(dt).toISOString();

  const removeFile = useCallback(() => {
    setFileState(null);
  }, []);

  const handleFileDrop = useCallback((file) => {
    console.log('File received:', file);
    setFileState(file);
  }, []);

  const renderFileDropZone = useCallback(() => {
    return !fileState ? (
      <FileDropZone setFileHandler={handleFileDrop} />
    ) : (
      <div className='file-detail'>
        <div className='close-icon' onClick={removeFile}>
          <img src={closeIcon} alt="Remove file" />
        </div>
        <div className='details'>
          <div>{fileState.name}</div>
          <div>{formatDate(fileState.lastModified)}</div>
        </div>
      </div>
    );
  }, [fileState, handleFileDrop, removeFile]);

  const showSuccessMessage = useCallback((msg) => {
    setSuccessMessage({ msg, show: true });
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setSuccessMessage({ msg: '', show: false });
      }
    }, 3600);
    return () => clearTimeout(timeoutId);
  }, [isMounted]);

  const showErrorMessage = useCallback((msg) => {
    setErrorMessage({ msg, show: true });
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setErrorMessage({ msg: '', show: false });
      }
    }, 3600);
    return () => clearTimeout(timeoutId);
  }, [isMounted]);

  const uploadFile = useCallback(() => {
    if (!fileState) return;

    const reader = new FileReader();

    console.log(item)

    reader.onloadend = () => {
      const base64File = reader.result.split(',')[1]; // Extract Base64 part
      if (base64File) {
        reportsService.makerUploadEventBasedReport(
          item.isin,
          item.bpId,
          item.reportTitle,
          item.issuer,
          item.holder,
          item.dateFrom,
          item.dateTo,
          item.paymentDate,
          base64File,
          fileState.name
        )
        .then(response => {
          console.log('Response:', response);
          if (isMounted) {
            showSuccessMessage('File uploaded successfully!');
          }
        })
        .catch(error => {
          console.error('Upload failed:', error);
          if (isMounted) {
            showErrorMessage('File upload failed!');
          }
        });
      } else {
        console.error('Base64 encoding failed.');
        if (isMounted) {
          showErrorMessage('File encoding failed!');
        }
      }
    };

    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      if (isMounted) {
        showErrorMessage('Error reading file');
      }
    };

    reader.readAsDataURL(fileState); // Convert file to Base64

    setUploadFlag(true);
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setUploadFlag(false);
      }
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [fileState, report, item, isMounted, showSuccessMessage, showErrorMessage]);

  // Wrap the modal close functionality so that if the Excel report was successfully uploaded,
  // the page is reloaded immediately upon closing the modal.
  const handleClose = useCallback(() => {
    onClose();
    // Only reload if the Excel report was successfully uploaded.
    if (excelUploaded && report?.title === EXCEL_REPORT_TITLE) {
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
      }
      //window.location.reload();
    }
  }, [onClose, excelUploaded, report]);

  return (
    <div className='upload-modal-content'>
      <Row>
        {successMessage.show && (
          <div className='success-msg'>
            <img alt="Success" src={checkIcon} /> {successMessage.msg}
          </div>
        )}
        {errorMessage.show && (
          <div className='error-msg'>
            <img alt="Error" src={failIcon} /> {errorMessage.msg}
          </div>
        )}
      </Row>
      <div className='file-upload-area'>{renderFileDropZone()}</div>
      <Row>
        <Col>
          <div className='button-container'>
            <Button onClick={handleClose} variant="secondary">
              Close
            </Button>
            <Button
              className={styles['btn']}
              disabled={!fileState || uploadFlag}
              onClick={uploadFile}
            >
              {uploadFlag ? (
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              ) : (
                'Upload'
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default UploadModalContent;
